import './styles/main.css';
import { FEATURED_SLUGS, MEGA_POKEMON, TYPE_LABELS } from './data/megaPokemon.js';
import { loadMegaPokemon } from './services/pokemonApi.js';
import { featuredCard, pokemonCard } from './components/pokemonCard.js';
import { ProfileDialog } from './components/profileDialog.js';

const dom = {
  grid: document.querySelector('[data-pokemon-grid]'),
  featuredGrid: document.querySelector('[data-featured-grid]'),
  filters: document.querySelector('[data-type-filters]'),
  search: document.querySelector('[data-search]'),
  count: document.querySelector('[data-result-count]'),
  status: document.querySelector('[data-status]'),
  empty: document.querySelector('[data-empty]'),
  clearFilters: document.querySelector('[data-clear-filters]'),
  motionToggle: document.querySelector('[data-motion-toggle]'),
  header: document.querySelector('[data-header]'),
  dialog: document.querySelector('[data-profile-dialog]'),
};

const state = {
  pokemon: [],
  visible: [],
  query: '',
  type: 'all',
};

const profileDialog = new ProfileDialog(dom.dialog, () => state.visible);

const normalize = (value) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const findPokemon = (slug) => state.pokemon.find((pokemon) => pokemon.slug === slug);

const renderHero = () => {
  document.querySelectorAll('[data-hero-pokemon]').forEach((slot) => {
    const pokemon = findPokemon(slot.dataset.heroPokemon);
    if (!pokemon) return;
    slot.innerHTML = `<img src="${pokemon.image}" alt="" width="480" height="480" />`;
    slot.style.setProperty('--hero-accent', pokemon.accent);
  });
};

const renderFeatured = () => {
  const featured = FEATURED_SLUGS.map(findPokemon).filter(Boolean);
  dom.featuredGrid.innerHTML = featured.map(featuredCard).join('');
};

const renderFilters = () => {
  const presentTypes = new Set(state.pokemon.flatMap(({ types }) => types));
  const buttons = Object.entries(TYPE_LABELS)
    .filter(([type]) => presentTypes.has(type))
    .map(
      ([type, label]) =>
        `<button type="button" data-type="${type}" aria-pressed="false">${label}</button>`,
    )
    .join('');
  dom.filters.insertAdjacentHTML('beforeend', buttons);
};

const applyFilters = () => {
  const query = normalize(state.query);
  state.visible = state.pokemon.filter((pokemon) => {
    const matchesType = state.type === 'all' || pokemon.types.includes(state.type);
    const matchesQuery =
      !query ||
      normalize(pokemon.name).includes(query) ||
      String(pokemon.number).padStart(4, '0').includes(query.replace('#', ''));
    return matchesType && matchesQuery;
  });

  dom.grid.innerHTML = state.visible.map(pokemonCard).join('');
  dom.count.textContent = state.visible.length;
  dom.empty.hidden = state.visible.length > 0;
};

const resetFilters = () => {
  state.query = '';
  state.type = 'all';
  dom.search.value = '';
  dom.filters.querySelectorAll('button').forEach((button) => {
    const active = button.dataset.type === 'all';
    button.classList.toggle('is-active', active);
    button.setAttribute('aria-pressed', String(active));
  });
  applyFilters();
};

const openFromTrigger = (trigger) => {
  const pokemon = findPokemon(trigger?.dataset.pokemonSlug);
  if (pokemon) profileDialog.open(pokemon);
};

const setupInteractions = () => {
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-pokemon-slug]');
    if (trigger) openFromTrigger(trigger);
  });

  dom.grid.addEventListener('pointermove', (event) => {
    const card = event.target.closest('.pokemon-card');
    if (!card) return;
    const rect = card.getBoundingClientRect();
    card.style.setProperty('--mouse-x', `${event.clientX - rect.left}px`);
    card.style.setProperty('--mouse-y', `${event.clientY - rect.top}px`);
  });

  dom.filters.addEventListener('click', (event) => {
    const button = event.target.closest('button[data-type]');
    if (!button) return;
    state.type = button.dataset.type;
    dom.filters.querySelectorAll('button').forEach((item) => {
      const active = item === button;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-pressed', String(active));
    });
    applyFilters();
  });

  dom.search.addEventListener('input', (event) => {
    state.query = event.currentTarget.value;
    applyFilters();
  });

  dom.clearFilters.addEventListener('click', resetFilters);

  document.addEventListener('keydown', (event) => {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      dom.search.focus();
      document.querySelector('#catalogo').scrollIntoView({ behavior: 'smooth' });
    }
  });

  window.addEventListener(
    'scroll',
    () => dom.header.classList.toggle('is-scrolled', window.scrollY > 24),
    { passive: true },
  );
};

const setupMotionPreference = () => {
  const stored = localStorage.getItem('mega-pokedex:reduce-motion');
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const reduced = stored === null ? prefersReduced : stored === 'true';

  const update = (value) => {
    document.documentElement.classList.toggle('reduce-motion', value);
    dom.motionToggle.setAttribute('aria-pressed', String(value));
    dom.motionToggle.querySelector('span').textContent = value
      ? 'Activar movimiento'
      : 'Reducir movimiento';
    localStorage.setItem('mega-pokedex:reduce-motion', String(value));
  };

  update(reduced);
  dom.motionToggle.addEventListener('click', () => {
    update(!document.documentElement.classList.contains('reduce-motion'));
  });
};

const setupRevealObserver = () => {
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.14 },
  );
  document.querySelectorAll('.section-heading, .story__content, .story__visual').forEach((element) =>
    observer.observe(element),
  );
};

const initialize = async () => {
  document.querySelector('[data-year]').textContent = new Date().getFullYear();
  setupMotionPreference();
  setupInteractions();
  setupRevealObserver();

  try {
    state.pokemon = await loadMegaPokemon(MEGA_POKEMON);
    if (!state.pokemon.length) throw new Error('No se recibieron datos de Pokémon.');
    state.visible = [...state.pokemon];
    renderHero();
    renderFeatured();
    renderFilters();
    applyFilters();
    dom.status.hidden = true;
  } catch (error) {
    console.error(error);
    dom.status.classList.add('status-panel--error');
    dom.status.innerHTML = `
      <strong>No fue posible conectar con la PokéAPI.</strong>
      <p>Comprueba tu conexión e inténtalo nuevamente.</p>
      <button class="button button--ghost" type="button">Reintentar</button>
    `;
    dom.status.querySelector('button').addEventListener('click', () => window.location.reload());
  }
};

initialize();
